import { AUTOMATION_MODES, NODE_TYPES } from './sopFlow';

// Splits an mxCell's HTML `value` into plain-text lines, inserting a break
// at each block boundary (<div>/<br>/<li>/<p>) so lines drawn as separate
// rows in the diagram (e.g. a description followed by a "Macro 16" line)
// stay separate instead of being flattened into one string.
const htmlToLines = html => {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(
    `<div>${html}</div>`,
    'text/html'
  );
  const root = doc.body.firstElementChild;
  // Isolate each block element's own text on its own line — a newline only
  // *after* would merge a divider line like "----" into the sentence
  // before it, which breaks the automation-reference divider detection.
  root.querySelectorAll('div, li, p').forEach(el => {
    el.insertAdjacentText('beforebegin', '\n');
    el.insertAdjacentText('afterend', '\n');
  });
  root
    .querySelectorAll('br')
    .forEach(el => el.insertAdjacentText('afterend', '\n'));
  return (root.textContent || '')
    .split('\n')
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

const htmlToText = html => htmlToLines(html).join(' ');

const htmlToListItems = html => {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(
    `<div>${html}</div>`,
    'text/html'
  );
  return [...doc.querySelectorAll('li')]
    .map(li => li.textContent.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

// Designers mark a box as an automation step by adding a "----" divider
// line followed by a reference like "Macro 16" or "Canned Response: ...".
// There's no reliable way to resolve that reference to a real macro/canned
// response id from the diagram alone, so the import leaves the automation
// unlinked and surfaces the raw reference in the node label for the admin
// to pick manually — the existing "not configured" warning on automation
// nodes already flags this before save.
const extractAutomationReference = lines => {
  const dividerIndex = lines.findIndex(line => /^-{3,}$/.test(line));
  if (dividerIndex === -1 || dividerIndex >= lines.length - 1) return null;
  const reference = lines.slice(dividerIndex + 1).join(' ');
  if (!/macro|canned\s*response/i.test(reference)) return null;
  return { description: lines.slice(0, dividerIndex).join(' '), reference };
};

const isGreenish = style => {
  const match = style.match(/fillColor=(#[0-9a-fA-F]{6})/);
  if (!match) return false;
  const hex = match[1];
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return g > r && g >= b;
};

const geometryOf = cell => {
  const geometry = cell.querySelector(':scope > mxGeometry');
  return {
    x: parseFloat(geometry?.getAttribute('x') || '0'),
    y: parseFloat(geometry?.getAttribute('y') || '0'),
  };
};

// Parses a raw (uncompressed) draw.io/diagrams.net XML export into the same
// { nodes, edges } shape used by the flow canvas, using shape + color +
// content conventions rather than any drawio-specific semantics: rhombus =
// decision, greenish rectangle = start/end (resolved by edge direction),
// rectangle with a divider + macro/canned-response reference = automation,
// everything else = action. Nodes with more than one outgoing connection
// become a route even if the designer didn't use a diamond for the fork.
export const parseDrawioXml = xmlString => {
  const xmlDoc = new DOMParser().parseFromString(xmlString, 'text/xml');
  if (xmlDoc.querySelector('parsererror')) {
    throw new Error('INVALID_XML');
  }

  const cells = [...xmlDoc.querySelectorAll('mxCell')];
  if (!cells.length) throw new Error('EMPTY_DIAGRAM');

  // Shapes with a link, tooltip or custom properties are wrapped as
  // <UserObject id label><mxCell/></UserObject>: id and label live on the wrapper.
  const wrapperOf = cell =>
    ['UserObject', 'object'].includes(cell.parentElement?.tagName)
      ? cell.parentElement
      : cell;
  const idOf = cell => wrapperOf(cell).getAttribute('id');
  const valueOf = cell =>
    wrapperOf(cell).getAttribute('label') ?? cell.getAttribute('value') ?? '';

  const cellsById = new Map(cells.map(cell => [idOf(cell), cell]));
  const isEdgeCell = cell => cell.getAttribute('edge') === '1';
  const isVertexCell = cell => cell.getAttribute('vertex') === '1';

  const labelCellsByEdgeId = new Map();
  cells.forEach(cell => {
    if (!isVertexCell(cell)) return;
    const parent = cellsById.get(cell.getAttribute('parent'));
    if (!parent || !isEdgeCell(parent)) return;
    const parentId = idOf(parent);
    const list = labelCellsByEdgeId.get(parentId) || [];
    list.push(cell);
    labelCellsByEdgeId.set(parentId, list);
  });

  const warnings = [];
  const nodesById = new Map();

  cells.forEach(cell => {
    if (!isVertexCell(cell)) return;
    const parent = cellsById.get(cell.getAttribute('parent'));
    if (parent && isEdgeCell(parent)) return; // edge label carrier, not a node

    const id = idOf(cell);
    const style = cell.getAttribute('style') || '';
    const value = valueOf(cell);
    const position = geometryOf(cell);

    if (/rhombus/.test(style)) {
      nodesById.set(id, {
        id,
        type: NODE_TYPES.DECISION,
        position,
        data: { label: htmlToText(value), branches: [] },
        isBranching: true,
      });
      return;
    }

    const lines = htmlToLines(value);
    const automation = extractAutomationReference(lines);
    if (automation) {
      warnings.push(
        `"${automation.reference}" needs linking to a real macro or canned response.`
      );
      nodesById.set(id, {
        id,
        type: NODE_TYPES.AUTOMATION,
        position,
        data: {
          label: `${automation.description}\n(Import ref: ${automation.reference})`,
          automation: null,
          mode: AUTOMATION_MODES.MANUAL,
        },
      });
      return;
    }

    if (isGreenish(style)) {
      nodesById.set(id, {
        id,
        type: null, // resolved once edge direction is known
        position,
        data: { label: lines.join(' ') },
        isTerminal: true,
      });
      return;
    }

    const items = htmlToListItems(value);
    nodesById.set(id, {
      id,
      type: NODE_TYPES.ACTION,
      position,
      data: {
        label: items.length ? '' : lines.join(' '),
        items: items.length ? items : [''],
      },
    });
  });

  const validEdgeCells = cells.filter(cell => {
    if (!isEdgeCell(cell)) return false;
    const source = cell.getAttribute('source');
    const target = cell.getAttribute('target');
    if (
      !source ||
      !target ||
      !nodesById.has(source) ||
      !nodesById.has(target)
    ) {
      warnings.push('Skipped a disconnected or dangling connector.');
      return false;
    }
    return true;
  });

  const outgoingCount = new Map();
  validEdgeCells.forEach(cell => {
    const source = cell.getAttribute('source');
    outgoingCount.set(source, (outgoingCount.get(source) || 0) + 1);
  });

  nodesById.forEach(node => {
    if (!node.isTerminal) return;
    const hasIncoming = validEdgeCells.some(
      cell => cell.getAttribute('target') === node.id
    );
    const hasOutgoing = (outgoingCount.get(node.id) || 0) > 0;
    if (hasOutgoing && !hasIncoming) {
      node.type = NODE_TYPES.START;
    } else if (hasIncoming && !hasOutgoing) {
      node.type = NODE_TYPES.END;
    } else {
      node.type = hasIncoming ? NODE_TYPES.END : NODE_TYPES.START;
      warnings.push(
        `Ambiguous start/end shape "${node.data.label}" — guessed ${node.type}, please verify.`
      );
    }
  });

  nodesById.forEach(node => {
    if (node.isBranching || node.type === NODE_TYPES.AUTOMATION) return;
    if (node.type === NODE_TYPES.START || node.type === NODE_TYPES.END) return;
    if ((outgoingCount.get(node.id) || 0) <= 1) return;
    node.type = NODE_TYPES.ROUTE;
    node.data = { label: node.data.label, branches: [] };
    node.isBranching = true;
  });

  const startCount = [...nodesById.values()].filter(
    node => node.type === NODE_TYPES.START
  ).length;
  if (startCount === 0)
    warnings.push('No Start shape detected — add one manually.');
  if (startCount > 1)
    warnings.push('Multiple Start shapes detected — keep only one.');

  const edges = [];
  validEdgeCells.forEach(cell => {
    const id = idOf(cell);
    const source = cell.getAttribute('source');
    const target = cell.getAttribute('target');
    const sourceNode = nodesById.get(source);

    const labelCells = labelCellsByEdgeId.get(id) || [];
    const label =
      labelCells.map(lc => htmlToText(valueOf(lc))).find(Boolean) ||
      htmlToText(valueOf(cell));

    let sourceHandle = null;
    if (sourceNode.isBranching) {
      const branch = { id: crypto.randomUUID(), label };
      sourceNode.data.branches.push(branch);
      sourceHandle = branch.id;
    } else if (outgoingCount.get(source) > 1) {
      warnings.push(
        `"${sourceNode.data.label}" has multiple outgoing paths but isn't a branch node — only one will render correctly.`
      );
    }

    edges.push({ id, source, sourceHandle, target });
  });

  const positions = [...nodesById.values()].map(node => node.position);
  const minX = Math.min(...positions.map(p => p.x));
  const minY = Math.min(...positions.map(p => p.y));

  const nodes = [...nodesById.values()].map(({ id, type, position, data }) => ({
    id,
    type,
    position: { x: position.x - minX + 40, y: position.y - minY + 40 },
    data,
  }));

  return { nodes, edges, warnings };
};
